/**
 * Estimate Results Component
 *
 * Displays the complete material estimate with:
 * - Project Summary at top
 * - Bill of Materials (simplified - no unit prices)
 * - Cost Summary (total only)
 * - Product Placement Guide
 * - Editable Notes
 * - Trim for Windows & Doors instructions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useEstimator } from '../../context/EstimatorContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getEstimateSummary } from '../../calculations';
import { MaterialsList } from './MaterialsList';
import { CostSummary } from './CostSummary';
import { ActionButtons } from './ActionButtons';
import { ExportDataButton } from './ExportDataButton';
import { ResultsVisualCard } from './ResultsVisualCard';
import { GuestCTA } from './GuestCTA';
import { GuestEstimateModal } from './GuestEstimateModal';
import { GuestSaveModal } from './GuestSaveModal';
import { getCapturePolicy } from '../../data/productTable';
import { gateIsOpen } from '../../config/capturePolicy';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useResultsUnlocked } from '../../utils/devUnlockResults';
import { TrimCalculator, Opening } from '../ui/TrimCalculator';
import { TrimOpening } from '../../utils/pdf';
// TODO: migrate saveUserNotes to PHP API
const saveUserNotes = async (_id: string, _notes: string): Promise<void> => {
  console.warn('[saveUserNotes] Not yet migrated to PHP API');
};
import {
  trackVirtualPageView,
  trackResultsViewed,
  trackCurrencyToggled,
  trackStartOver,
  trackSaveModalOpened,
  trackSaveModalDismissed,
  trackEstimateSaved,
  setTrackingContext,
  SaveTrigger,
  CaptureMode,
} from '../../utils/gtmTracking';
import {
  RefreshCcw,
  ChevronLeft,
  User,
  Mail,
  Phone,
  MapPin,
  LayoutGrid,
  Ruler,
  Palette,
  Move,
  Grid3X3,
  Layers,
  SquareStack,
  Scissors,
  Square,
  CircleX,
  Map,
  FileText,
  AlertTriangle,
  Hash,
  StickyNote,
  DoorOpen,
  Receipt,
  ClipboardList,
  CheckCircle,
  Copy,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import { saveAuthEstimate, renameAuthEstimate, saveUserProfile, fetchGeoCountries } from '../../services/api';
import { fetchUserCategories } from '../../services/userCategoriesApi';
import { UserCategory, USER_CATEGORY_FALLBACK } from '../../data/userCategories';
import { GeoCountry, fallbackGeo } from '../../data/regions';
import { buildWizardSnapshot } from '../../utils/estimateSnapshot';
import { EstimateNameModal, EstimateDetails } from './EstimateNameModal';
import { WallConfig, MaterialItem } from '../../types';
import { CheckboxChecked, CheckboxUnchecked } from '../common/CheckboxIcons';
import { resolveNotes } from '../../data/bomSections';
import { calculationModeLabel, spaceTypeLabel } from './configDataPoints';
import { PlacementGuide } from './PlacementGuide';
import { CustomTablesSection } from './CustomTablesSection';
import { LocationMapSection } from '../maps/LocationMapSection';
import { OutputSectionsSection } from './OutputSectionsSection';
import { CommentsSection } from '../comments/CommentsSection';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import { currencyPolicy } from '../../utils/currencyPolicy';
import './EstimateResults.css';

// Helper to aggregate materials by productID
const aggregateMaterials = (items: MaterialItem[]): MaterialItem[] => {
  const aggregated: Record<string, MaterialItem> = {};

  items.forEach(item => {
    const key = item.productID;
    if (aggregated[key]) {
      aggregated[key].quantity += item.quantity;
    } else {
      aggregated[key] = { ...item };
    }
  });

  return Object.values(aggregated);
};

export const EstimateResults: React.FC = () => {
  const { t, i18n } = useTranslation('results');
  const { state, reset, clearResults, setCurrency, updateUserNotes, setSavedEstimate, updateProjectInfo } = useEstimator();
  const { savedEstimate } = state;
  const { isAuthenticated, profile, refreshProfile } = useAuth();
  const { showToast } = useToast();
  const { partner } = usePartnerBranding();
  const { estimateResult, currency } = state;

  // Single-currency retailer: force that currency (no toggle anywhere).
  const { forced: forcedCurrency } = currencyPolicy(partner?.currenciesSupported);
  useEffect(() => {
    if (forcedCurrency && currency !== forcedCurrency) setCurrency(forcedCurrency);
  }, [forcedCurrency, currency, setCurrency]);
  // Analytics: price keys report the figure on screen, so the display currency
  // must be in the tracking context before results_viewed fires. The live
  // wizard always shows the rounded total.
  useEffect(() => {
    setTrackingContext({ currency, totalFormat: 'rounded' });
  }, [currency]);
  const [userNotes, setUserNotes] = useState(estimateResult?.userNotes || '');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [trimOpenings, setTrimOpenings] = useState<TrimOpening[]>([]);
  const [guestEstimateSaved, setGuestEstimateSaved] = useState(false);
  const [guestSavedGuid, setGuestSavedGuid] = useState<string | null>(null);
  const [guestSaveModalOpen, setGuestSaveModalOpen] = useState(false);
  const [guestCopySuccess, setGuestCopySuccess] = useState(false);

  // --- Capture policy (per retailer, arrives with the price book) ------------
  // `minimal` retailers ask a guest for an estimate name and an email, and the
  // detailed materials list is NOT gated behind that form — declining still
  // leaves the full estimate on screen. Saving buys retrieval and the emailed
  // link. `full` retailers keep today's ten-field GuestEstimateModal and the gate.
  const capturePolicy = getCapturePolicy();
  const minimalCapture = capturePolicy.mode === 'minimal';
  // Dev Console → "Full Results": a super admin can open the gate without
  // submitting the capture form. ANDed with isSuperAdmin so the sessionStorage
  // flag is inert for everyone else, and it widens only what renders — saving
  // still goes through the server's own policy enforcement.
  const { isSuperAdmin } = useAdminAuth();
  const devUnlocked = useResultsUnlocked() && isSuperAdmin;
  const showFullResults = isAuthenticated || gateIsOpen(capturePolicy) || devUnlocked;
  // The override also suppresses BOTH forced "Save your estimate" sheets, which
  // are separate modals on separate triggers and each undismissable by design:
  //   - guests  → GuestSaveModal / GuestEstimateModal, no close button when the
  //               retailer's policy sets allowSkip false;
  //   - members → EstimateNameModal in 'create' mode, which handleNameClose
  //               refuses to close because a saved record is required.
  // Either one sits over the very results the override exists to show.
  // Suppressing them here beats a dev-only escape hatch inside each modal —
  // those are components real customers use, and neither needs a branch that
  // only ever runs for a super admin.
  const captureModalOpen = guestSaveModalOpen && !devUnlocked;
  // The minimal prompt opens itself once per results view; dismissing it must
  // stick, or "Don't save" would just re-appear on the next render.
  const [minimalPromptDismissed, setMinimalPromptDismissed] = useState(false);
  // Analytics: how the save prompt was opened (auto vs the bar's button) and
  // which form the retailer's policy showed. Both are per-retailer data.
  const saveTriggerRef = useRef<SaveTrigger>('email_button');
  const captureMode: CaptureMode = minimalCapture ? 'save_modal' : 'project_planner';
  useEffect(() => {
    if (captureModalOpen) trackSaveModalOpened(saveTriggerRef.current, captureMode);
  }, [captureModalOpen, captureMode]);
  // Dismissed = closed without a save. The full form's success screen also
  // closes through onClose, but by then guestEstimateSaved is true.
  const handleGuestModalDismiss = () => {
    if (!guestEstimateSaved) trackSaveModalDismissed(saveTriggerRef.current, captureMode);
  };

  // Logged-in estimate naming (auto-save on first results view + rename).
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameModalMode, setNameModalMode] = useState<'create' | 'rename'>('create');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  // Option sources for the save modal (admin-managed; silent fallback).
  const [categories, setCategories] = useState<UserCategory[]>(USER_CATEGORY_FALLBACK);
  const [geo, setGeo] = useState<GeoCountry[]>(fallbackGeo);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    // Category and country names are admin-managed text translated
    // server-side, so a language switch has to re-fetch them.
    fetchUserCategories().then(c => { if (!cancelled && c) setCategories(c); });
    fetchGeoCountries().then(g => { if (!cancelled && g) setGeo(g); });
    return () => { cancelled = true; };
  }, [isAuthenticated, i18n.language]);
  // Profile is missing "I Am A" → collect it inline on first save.
  const needUserCategory = !(profile?.userCategory && profile.userCategory.trim());

  // Convert Opening to TrimOpening for PDF
  const handleOpeningsChange = (openings: Opening[]) => {
    setTrimOpenings(openings.map(o => ({
      type: o.type,
      name: o.name,
      width: o.width,
      height: o.height,
    })));
  };

  // --- GTM: EVENT 3 & 5 — Virtual page view + results viewed on mount ---
  const trackedRef = useRef(false);
  useEffect(() => {
    if (estimateResult && !trackedRef.current) {
      trackedRef.current = true;
      trackVirtualPageView('results', state.calculationMode);
      trackResultsViewed(
        state.calculationMode,
        state.applicationConfig.spaceType,
        estimateResult,
      );
    }
  }, [estimateResult, state.calculationMode, state.applicationConfig.spaceType]);

  useEffect(() => {
    if (estimateResult?.userNotes) {
      setUserNotes(estimateResult.userNotes);
    }
  }, [estimateResult?.userNotes]);

  // First-time name prompt for logged-in users. Opens once when results appear
  // and no estimate has been saved yet; savedEstimate persists across "back to
  // edit" + recalc, so it never re-prompts after the first save.
  useEffect(() => {
    if (isAuthenticated && estimateResult && !savedEstimate && !devUnlocked) {
      setNameModalMode('create');
      setNameError(null);
      setNameModalOpen(true);
    }
  }, [isAuthenticated, estimateResult, savedEstimate, devUnlocked]);

  // Guest equivalent for minimal-capture retailers: same moment, same shape as
  // the logged-in prompt — but dismissible, because nothing is withheld if they
  // decline.
  useEffect(() => {
    if (
      !isAuthenticated && minimalCapture && estimateResult
      && !guestEstimateSaved && !minimalPromptDismissed && !devUnlocked
    ) {
      saveTriggerRef.current = 'auto_prompt';
      setGuestSaveModalOpen(true);
    }
  }, [isAuthenticated, minimalCapture, estimateResult, guestEstimateSaved, minimalPromptDismissed, devUnlocked]);

  if (!estimateResult) {
    return null;
  }

  const summary = getEstimateSummary(estimateResult);
  const projectInfo = estimateResult.projectInfo;
  const displayName = savedEstimate?.name || projectInfo?.projectName || '';

  const handleStartOver = () => {
    trackStartOver(state.calculationMode, estimateResult);
    reset();
  };

  const handleCurrencyChange = (cur: 'USD' | 'CAD') => {
    trackCurrencyToggled(state.calculationMode, cur);
    setCurrency(cur);
  };

  const handleGoBack = () => {
    clearResults();
  };

  // --- Logged-in estimate naming / renaming ---
  const openRename = () => {
    setNameModalMode('rename');
    setNameError(null);
    setNameModalOpen(true);
  };

  // Create mode can't be dismissed (we need a saved record); rename can.
  const handleNameClose = () => {
    if (nameModalMode === 'rename' && !nameSaving) setNameModalOpen(false);
  };

  const handleNameSave = async (d: EstimateDetails) => {
    setNameSaving(true);
    setNameError(null);
    try {
      if (nameModalMode === 'create') {
        const effectiveCategory = needUserCategory ? d.userCategory : (profile?.userCategory || '');
        const { guid } = await saveAuthEstimate({
          estimateName: d.name,
          estimateData: estimateResult,
          wizardState: buildWizardSnapshot(state),
          retailerId: partner?.id ?? null,
          projectType: d.projectType,
          userCategory: effectiveCategory,
          country: d.country,
          region: d.region,
          city: d.city,
          postalCode: d.postalCode,
        });
        setSavedEstimate({ guid, name: d.name, projectType: d.projectType });
        trackEstimateSaved(state.calculationMode, state.applicationConfig.spaceType, guid, estimateResult);

        // Seed profile defaults: set "I Am A" if it was missing, and the default
        // location only when the profile has none yet (per-estimate overrides
        // never overwrite an existing profile default). Best-effort.
        const patch: Record<string, string> = {};
        if (needUserCategory && d.userCategory) patch.userCategory = d.userCategory;
        if (!(profile?.country && profile.country.trim())) {
          patch.country = d.country; patch.region = d.region; patch.city = d.city; patch.postalCode = d.postalCode;
        }
        if (Object.keys(patch).length > 0) {
          saveUserProfile(patch).then(() => refreshProfile()).catch(() => { /* best-effort */ });
        }
      } else if (savedEstimate) {
        await renameAuthEstimate(savedEstimate.guid, d.name, d.projectType);
        setSavedEstimate({ guid: savedEstimate.guid, name: d.name, projectType: d.projectType });
      }
      updateProjectInfo({ projectName: d.name });
      setNameModalOpen(false);
    } catch {
      setNameError(t('save.errors.failed'));
    } finally {
      setNameSaving(false);
    }
  };

  // Saved-guest success card: build the URL and copy handler
  const guestEstimateUrl = guestSavedGuid
    ? `${window.location.origin}/estimate/${guestSavedGuid}`
    : '';
  const handleGuestCopyLink = () => {
    if (!guestEstimateUrl) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(guestEstimateUrl);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = guestEstimateUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setGuestCopySuccess(true);
    setTimeout(() => setGuestCopySuccess(false), 2500);
    showToast(t('lead.success.copied'), undefined, 'success');
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserNotes(e.target.value);
    setNotesSaved(false);
  };

  const handleSaveNotes = async () => {
    if (!estimateResult.id) return;

    setNotesSaving(true);
    try {
      await saveUserNotes(estimateResult.id, userNotes);
      updateUserNotes(userNotes);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setNotesSaving(false);
    }
  };

  // Space type and calculation mode go through the shared translated labels —
  // the same ones the config cards, PDF and CSV use.
  const spaceType = spaceTypeLabel(state.applicationConfig.spaceType);
  const projectType = calculationModeLabel(state.calculationMode);

  // Calculate total screws (combined)
  const totalScrews = estimateResult.billOfMaterials.screws.total;

  // Format date for print
  const printDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const systemNotes = resolveNotes(estimateResult);

  return (
    <div className="estimate-results">
      {/* Print Header (only visible when printing) */}
      <div className="print-header">
        <img src="/images/trusscore-colour-dark.svg" alt="Trusscore" className="print-header-logo" />
        <div className="print-header-subtitle">{t('printSubtitle')}</div>
      </div>

      {/* Project Summary Section */}
      <div className="project-summary">
        <div className="project-summary-header">
          <div>
            <h1 className="project-title d-inline-flex align-items-center gap-2">
              {displayName || t('defaultTitle')}
              {isAuthenticated && savedEstimate && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted"
                  title={t('renameEstimate')}
                  onClick={openRename}
                >
                  <Pencil size={16} />
                </button>
              )}
            </h1>
            {/* Project details as compact subtitle */}
            <div className="project-details-subtitle">
              {projectInfo?.customerName && <span><User size={13} /> {projectInfo.customerName}</span>}
              {projectInfo?.email && <span><Mail size={13} /> {projectInfo.email}</span>}
              {projectInfo?.phone && <span><Phone size={13} /> {projectInfo.phone}</span>}
              {projectInfo?.location && <span><MapPin size={13} /> {projectInfo.location}</span>}
            </div>
          </div>
          <div className="header-actions">
            {!guestEstimateSaved && (
              <button
                type="button"
                className="btn btn-secondary d-inline-flex align-items-center gap-1"
                onClick={handleGoBack}
              >
                <ChevronLeft size={16} />
                {t('backToEdit')}
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={handleStartOver}
            >
              <RefreshCcw size={16} />
              {t('startOver')}
            </button>
          </div>
        </div>
      </div>

      {/* Results visual card — pulled OUT of .project-summary so it lives in
          its own non-card container, matching the unauthenticated full-estimate
          layout. */}
      <div className="results-visual-container mb-4">
        <ResultsVisualCard
          estimateResult={estimateResult}
          wizardState={state}
          currency={currency}
          onCurrencyChange={handleCurrencyChange}
          pulseKey={`${estimateResult.id ?? 'live'}-${currency}`}
        />
      </div>

      {/* Guest View: Save button + CTA card */}
      {!isAuthenticated && (
        <div className="results-guest">
          {/* Top slot: save prompt when unsaved → success card when saved.
              The card transforms in place so the user's eye never loses the
              confirmation. */}
          {!guestEstimateSaved ? (
            <div className="guest-save-bar" style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              textAlign: 'center',
              marginBottom: 'var(--spacing-md)',
            }}>
              <p style={{
                color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', lineHeight: 1.6,
                margin: '0 0 var(--spacing-md)', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto',
              }}>
                {t(minimalCapture ? 'guestBar.minimalPrompt' : 'guestBar.fullPrompt')}
              </p>
              <button
                type="button"
                className="btn btn-primary btn-lg d-inline-flex align-items-center gap-2 mb-0"
                onClick={() => { saveTriggerRef.current = 'email_button'; setGuestSaveModalOpen(true); }}
              >
                <ClipboardList size={20} />
                {t(minimalCapture ? 'guestBar.minimalCta' : 'guestBar.fullCta')}
              </button>
            </div>
          ) : (
            <div className="guest-save-bar guest-save-bar--success" style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              textAlign: 'center',
              marginBottom: 'var(--spacing-md)',
              color: '#ffffff',
            }}>
              <div style={{ color: 'var(--partner-primary, #feb100)', marginBottom: 'var(--spacing-sm)' }}>
                <CheckCircle size={36} />
              </div>
              <h3 style={{
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: 700,
                margin: '0 0 var(--spacing-xs)',
              }}>
                {t('cta.savedTitle')}
              </h3>
              <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                margin: '0 0 var(--spacing-md)',
                maxWidth: '620px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                {t('guestBar.savedBody')}
              </p>
              {guestEstimateUrl && (
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  margin: '0 auto var(--spacing-md)',
                  maxWidth: '620px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                  color: 'rgba(255,255,255,0.9)',
                }}>
                  {guestEstimateUrl}
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'var(--spacing-sm)',
                flexWrap: 'wrap',
              }}>
                <button
                  type="button"
                  onClick={handleGuestCopyLink}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer', border: '1px solid var(--color-primary)',
                    background: guestCopySuccess ? 'var(--color-primary)' : 'transparent',
                    color: guestCopySuccess ? '#1a1a2e' : 'var(--color-primary)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {guestCopySuccess ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {guestCopySuccess ? t('cta.linkCopied') : t('guestBar.copyLink')}
                </button>
                <a
                  href={guestEstimateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                    textDecoration: 'none',
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'transparent',
                    color: '#ffffff',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ExternalLink size={16} />
                  {t('lead.success.view')}
                </a>
              </div>
            </div>
          )}

          {/* Upsell card hidden until login/account features are fleshed out.
              Restore by removing the `false &&` guard below. */}
          {false && !guestEstimateSaved && (
            <div className="row g-3">
              <div className="col-12 d-flex">
                <GuestCTA onEstimateSaved={() => setGuestEstimateSaved(true)} />
              </div>
            </div>
          )}

          {minimalCapture ? (
            <GuestSaveModal
              isOpen={captureModalOpen}
              policy={capturePolicy}
              saveTrigger={saveTriggerRef.current}
              onClose={() => {
                handleGuestModalDismiss();
                setGuestSaveModalOpen(false);
                setMinimalPromptDismissed(true);
              }}
              onSuccess={(guid) => {
                setGuestSavedGuid(guid);
                setGuestEstimateSaved(true);
                setGuestSaveModalOpen(false);
              }}
            />
          ) : (
          <GuestEstimateModal
            isOpen={captureModalOpen}
            saveTrigger={saveTriggerRef.current}
            onClose={() => { handleGuestModalDismiss(); setGuestSaveModalOpen(false); }}
            onSuccess={(guid) => {
              setGuestSavedGuid(guid);
              setGuestEstimateSaved(true);
              // Intentionally NOT closing the modal here — the modal's own
              // success screen (with close button) stays visible so the user
              // sees immediate feedback. When they close it, the inline
              // success card above is already in place.
            }}
          />
          )}
        </div>
      )}

      {/* Full Content Grid — members always; guests too where the retailer's
          capture policy leaves the gate open (minimal capture). */}
      {showFullResults && (
        <>
          <div className="results-grid">
            {/* Materials List */}
            <div className="results-main">
              <MaterialsList
                billOfMaterials={estimateResult.billOfMaterials}
                currency={currency}
                showPrices={false}
                headerActions={<ExportDataButton estimateId={estimateResult.id} />}
              />
            </div>

            {/* Cost Summary Sidebar */}
            <div className="results-sidebar">
              {/* Note: currency toggle lives in ResultsVisualCard; sidebar
                  CostSummary no longer owns it to avoid duplicate controls. */}
              <CostSummary
                totals={estimateResult.totals}
                currency={currency}
                showItemized={false}
              />

              <ActionButtons
                estimateId={estimateResult.id}
                trimOpenings={trimOpenings}
                userNotes={userNotes}
              />
            </div>
          </div>

          {/* Custom tables below the BOM */}
          <CustomTablesSection estimate={estimateResult} placement="belowBom" />

          {/* Inline location map (retailers / installers) — screen only, never print/PDF */}
          <LocationMapSection placement="belowBom" estimateGuid={guestSavedGuid ?? estimateResult.id ?? null} retailerId={partner?.id ?? null} />
        </>
      )}

      {/* === Full-results sections below (see showFullResults) === */}
      {showFullResults && (
      <>

      {/* Product Placement Guide */}
      <PlacementGuide estimateResult={estimateResult} />

      {/* Custom tables below the placement guide */}
      <CustomTablesSection estimate={estimateResult} placement="belowPlacement" />

      {/* Trim Calculator for Windows & Doors */}
      <div className="trim-calculator-section" style={{ display: 'none' }}>
        <TrimCalculator onOpeningsChange={handleOpeningsChange} />
      </div>

      {/* Editable Notes Section */}
      <div className="user-notes">
        <div className="notes-header">
          <h2 className="section-title">
            <StickyNote size={22} className="section-title-icon" />
            {t('pdf.notes.project')}
          </h2>
          <button
            type="button"
            className={`btn btn-sm ${notesSaved ? 'btn-success' : 'btn-primary'}`}
            onClick={handleSaveNotes}
            disabled={notesSaving}
          >
            {notesSaving ? t('save.saving') : notesSaved ? t('notesPanel.saved') : t('notesPanel.save')}
          </button>
        </div>
        <textarea
          className="notes-textarea"
          value={userNotes}
          onChange={handleNotesChange}
          placeholder={t('notesPanel.placeholder')}
          rows={4}
        />
      </div>

      {/* System Notes — same source as the PDF's, via resolveNotes(). */}
      {systemNotes.length > 0 && (
        <div className="results-notes">
          <h3 className="notes-title">
            <FileText size={18} className="notes-title-icon" />
            {t('pdf.notes.system')}
          </h3>
          <ul className="notes-list">
            {systemNotes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Output footer sections (admin-authored; screen / print, mirrored in the PDF) */}
      <OutputSectionsSection />

      {/* Disclaimer */}
      <div className="results-disclaimer">
        <AlertTriangle size={20} className="disclaimer-icon" />
        <p>
          <Trans i18nKey="disclaimer.full" ns="results" components={{ b: <strong /> }} />
        </p>
      </div>

      {/* Comments (requires a saved estimate with a GUID) */}
      {estimateResult.id && (
        <CommentsSection estimateGuid={estimateResult.id} />
      )}

      </>
      )}
      {/* === End authenticated-only sections === */}

      {/* Guest view: show comments after estimate is saved */}
      {!isAuthenticated && guestSavedGuid && (
        <CommentsSection estimateGuid={guestSavedGuid} />
      )}

      {/* Print Footer (only visible when printing) */}
      <div className="print-footer">
        <p>{t('printFooter')}</p>
        <p className="print-date">{t('printDate', { date: printDate })}</p>
        {estimateResult.id && (
          <p>{t('printReferenceId', { id: estimateResult.id })}</p>
        )}
      </div>

      {/* Logged-in: details on first view / edit later.
          The dev override suppresses only 'create' — the undismissable one.
          Rename is opened deliberately and closes fine, so leaving it working
          avoids a dead button if an estimate was saved earlier in the session
          and the override is flipped on afterwards. */}
      {isAuthenticated && (
        <EstimateNameModal
          open={nameModalOpen && !(devUnlocked && nameModalMode === 'create')}
          mode={nameModalMode}
          initialName={
            nameModalMode === 'rename'
              ? (savedEstimate?.name || '')
              : (projectInfo?.projectName?.trim() || t('defaultEstimateName', { space: spaceType }))
          }
          initialProjectType={savedEstimate?.projectType || ''}
          profile={profile}
          needUserCategory={needUserCategory}
          categories={categories}
          geo={geo}
          saving={nameSaving}
          error={nameError}
          onSave={handleNameSave}
          onClose={handleNameClose}
        />
      )}
    </div>
  );
};

export default EstimateResults;
